import { h, Component } from 'preact';
import { History } from 'history';
import classNames from 'classnames';
import { Icon } from '@grubhubprod/cookbook/dist/cookbook-preact/esm/components/icon/icon';
import { SubscriptionData } from 'src/utilities/types';
import { get } from 'src/shared/dependencies/nodash/get';
import { appConfigFactory } from 'src/shared/factories/appConfig.factory';
import { Platform, SubscriptionPurchasePaths } from 'src/environment';
import { NoIndexHelmet } from 'src/shared/meta/NoIndexHelmet';
import { NativeBridge } from 'src/shared/utilities/native-bridge.utility';
import { NativeMessages } from 'src/native-messages';
import { PageLoader } from 'src/components/PageLoader/PageLoader';
import { WebFooter } from 'src/components/WebFooter';
import { WebHeader } from 'src/components/WebHeader';
import { isMobileViewport } from 'src/shared/utilities/screenWidth.utility';
import { RouteComponentProps } from 'src/shared/router';
import { LocalStorageUtility } from 'src/shared/utilities/localStorage.utility';
import { SubscriptionService } from 'src/shared/services/SubscriptionService';
import { SubscriptionBanner } from '../../components/SubscriptionBanner/SubscriptionBanner';
import {
  getFormattedSubscriptionData,
  setEndOfFlowDestination,
} from '../../utilities/subscriptions-utility';
import { SubscriptionAdapter } from '../../Adapters/SubscriptionAdapter';
import { createAdapter } from '../../Adapters/createAdapter';
import { SubscriptionAnalytics } from '../../Analytics/SubscriptionAnalytics';
import { TYPES } from '../../di/types';
import { lazyInject } from '../../di';

export interface SubscriptionConfirmationProps extends RouteComponentProps {
  credentialsReceived: boolean;
  isAnonymous: boolean;
  history: History;
}

export interface SubscriptionConfirmationState {
  subscription: SubscriptionData;
}

export class SubscriptionConfirmation extends Component<
  SubscriptionConfirmationProps,
  SubscriptionConfirmationState
> {
  @lazyInject(TYPES.SubscriptionAnalytics)
  private readonly analytics: SubscriptionAnalytics;
  @lazyInject(TYPES.SubscriptionService)
  private readonly subscriptionService: SubscriptionService;
  private appConfig = appConfigFactory();
  @lazyInject(TYPES.NativeBridge)
  private nativeBridge: NativeBridge;
  private adapter: SubscriptionAdapter = createAdapter(
    this.appConfig.platform as Platform,
    { history: this.props.history },
  );
  private storage = new LocalStorageUtility();

  public async componentDidMount() {
    setEndOfFlowDestination();
    const subscription = await this.getSubscription();
    this.setState({
      subscription,
    });
    // we have redirected. As a result no need to continue the mounting and abort.
    if (this.isRedirectNeeded()) {
      return;
    }
    this.fireClickstreamPostPurchaseEvents(this.getQueryParams());

    this.analytics.logPageView(
      'subscription order confirmation',
      subscription,
      null,
      subscription?.subscription_id,
    );
    this.nativeBridge.toNative(NativeMessages.SUBSCRIPTION_PURCHASE_SUCCESS);

    const brazeNewUserSignedUp = this.storage.get('brazeNewUserSignedUp');

    if (brazeNewUserSignedUp) {
      this.analytics.logBrazeEvent(brazeNewUserSignedUp);
      this.storage.remove('brazeNewUserSignedUp');
    }
  }
  /**
   * Fire clickstream purchase events that are cutoff from the tonkotsu hosted /plus/purchase page. Then replaces state to wipe query params
   * @param searchParams url query param object. We check for sendClickstreamPurchaseEvent or sendClickstreamAutoOptInEvent and send clickstream event based on those params
   */
  private fireClickstreamPostPurchaseEvents(searchParams: URLSearchParams) {
    const { subscription } = this.state;
    const { history } = this.props;

    const purchaseEvent = searchParams.get('sendClickstreamPurchaseEvent');
    if (purchaseEvent) {
      this.analytics.logSubscriptionPostPurchaseSuccessClickstream(
        subscription,
      );
    }
    const autoOptInEvent = searchParams.get('sendClickstreamAutoOptInEvent');
    if (autoOptInEvent) {
      this.analytics.logSubscriptionPostAutoOptInSuccessClickstream(
        subscription,
      );
    }
    history.replace(SubscriptionPurchasePaths.CONFIRMATION);
  }
  /**
   * Get query params from url
   */
  private getQueryParams(): URLSearchParams {
    const { location } = this.props;
    return new URLSearchParams(location.search);
  }
  public isRedirectNeeded(): boolean {
    const { credentialsReceived, isAnonymous } = this.props;
    if (credentialsReceived && isAnonymous) {
      this.adapter.redirectToSubscriptionInfo();
      return true;
    }
    return false;
  }

  public async getSubscription(): Promise<SubscriptionData> {
    try {
      const {
        subscription,
      } = await this.subscriptionService.getActiveSubscription();
      return subscription;
    } catch (e) {
      return null;
    }
  }

  public render() {
    const { credentialsReceived, isAnonymous } = this.props;
    const { subscription } = this.state;

    if (!subscription) {
      return <PageLoader />;
    }

    const headerData = getFormattedSubscriptionData(subscription);
    const isWeb = this.appConfig.platform === Platform.WEB;

    return (
      <div>
        <NoIndexHelmet />
        {!credentialsReceived || isAnonymous || !subscription ? (
          <PageLoader />
        ) : (
          <div>
            {isWeb && (
              <nav
                class="mainNav" /*Necessary in conjunction with outerWrapper below*/
              >
                {
                  <WebHeader
                    signInRedirectUrl={SubscriptionPurchasePaths.CONFIRMATION}
                    isAuthenticated={credentialsReceived && !isAnonymous}
                  />
                }
              </nav>
            )}

            <section className="outerWrapper" at-subscription-confirmation>
              <SubscriptionBanner />
              <div className="u-inset-4 s-container-sm">
                <div className="u-stack-y-8 u-flex-center-center">
                  <div className="subscription-confirmation-checkmark ">
                    <Icon icon="check" size="xl" />
                  </div>
                </div>
                <div className="u-stack-y-6">
                  <h2
                    className={classNames('u-text-center', {
                      'massive-heading': isMobileViewport(window),
                    })}
                  >
                    {get(subscription, 'texts.confirmation_page.title_text')}
                  </h2>
                </div>
                <div className="u-stack-y-8 u-line-bottom u-line--light">
                  <p
                    className={classNames({
                      'u-stack-y-6': !isMobileViewport(window),
                      'u-stack-y-8': isMobileViewport(window),
                    })}
                  >
                    {get(subscription, 'texts.confirmation_page.body_text')}
                  </p>
                </div>
                <div className="u-stack-y-8">
                  {Object.keys(headerData.bullets).map((bullet, index) => {
                    const { text, image_url } = headerData.bullets[bullet];

                    if (text === '' || image_url === '') {
                      return null;
                    }

                    return (
                      <div
                        className="u-flex u-flex-align-xs--center u-stack-y-4"
                        key={index}
                      >
                        <img
                          className="u-stack-x-4 cb-icon--lg"
                          src={image_url}
                          alt="membership benefit icon"
                        />
                        <span className="lead">{text}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="u-stack-y-12">
                  <button
                    at-subscription-confirmation-cta
                    class="s-btn s-btn--large s-btn-primary s-btn--block"
                    onClick={this.adapter.onFlowComplete}
                  >
                    {get(subscription, 'texts.confirmation_page.cta_text')}
                  </button>
                </div>
              </div>
              {isWeb && (
                <footer>
                  <WebFooter />
                </footer>
              )}
            </section>
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./modules/Subscriptions/pages/Confirmation/SubscriptionConfirmation.tsx